// client/src/services/discordService.js
import API_CONFIG from '../config/apiConfig';
import { apiClient } from '../config/apiConfig';

const { endpoints } = API_CONFIG;

const discordService = {
  // Check Discord bot status
  getStatus: async () => {
    try {
      const response = await apiClient.get(endpoints.discord.status);
      return response.data;
    } catch (error) {
      console.error('Error getting Discord status:', error);
      throw error;
    }
  },

  // Get bot invite URL
  getInvite: async () => {
    try {
      const response = await apiClient.get(endpoints.discord.invite);
      return response.data; // The URL is directly in response.data
    } catch (error) {
      console.error('Error getting Discord invite:', error);
      throw error;
    }
  },

  // Get Discord channels
  getChannels: async () => {
    try {
      // First check if we have settings
      const settings = await discordService.getSettings();
      if (!settings?.guildId) {
        throw new Error('Discord server not configured. Please set up the Discord server first.');
      }
      const response = await apiClient.get(endpoints.discord.channels(settings.guildId));
      return response.data;
    } catch (error) {
      console.error('Error fetching Discord channels:', error);
      throw error;
    }
  },

  // Get Discord categories
  getCategories: async (guildId) => {
    try {
      const response = await apiClient.get(endpoints.discord.categories(guildId));
      return response.data;
    } catch (error) {
      console.error('Error fetching Discord categories:', error);
      throw error;
    }
  },

  // Get guild emojis
  getGuildEmojis: async () => {
    try {
      // First check if we have settings
      const settings = await discordService.getSettings();
      if (!settings?.guildId) {
        throw new Error('Discord server not configured. Please set up the Discord server first.');
      }
      const response = await apiClient.get(endpoints.discord.emojis);
      return response.data;
    } catch (error) {
      console.error('Error fetching guild emojis:', error);
      throw error;
    }
  },

  // Get emojis from a Discord message
  getMessageEmojis: async (messageId, channelId) => {
    try {
      const messageDetails = await discordService.getMessageDetails(messageId, channelId);
      return messageDetails.emojiReactions || [];
    } catch (error) {
      console.error('Error getting message emojis:', error);
      throw error;
    }
  },

  // Sync existing Discord message for campaign briefing
  syncExistingMessage: async (campaignId, messageId, channelId, targetEmoji, syncMethod = 'manual') => {
    try {
      // Only campaignId, messageId, and targetEmoji are required
      // channelId can be optional - the server will use the default briefing channel if not provided
      if (!campaignId || !messageId) {
        throw new Error('Missing required parameters: campaignId and messageId are required');
      }
      
      if (!targetEmoji) {
        console.warn('No target emoji provided for sync operation');
      }
      
      // Make API call to sync the Discord message
      console.log(`Making API call to sync Discord message for campaign ${campaignId}`);
      console.log('Sync parameters:', { messageId, channelId, targetEmoji, syncMethod });
      
      // Prepare request payload - only include channelId if it's provided
      const payload = {
        messageId,
        targetEmoji,
        syncMethod
      };
      
      // Only add channelId to payload if it exists and is not empty
      if (channelId) {
        payload.channelId = channelId;
      }
      
      const response = await apiClient.post(`/discord/campaigns/${campaignId}/sync-message`, payload);
      
      if (!response || !response.data) {
        throw new Error('No response received from Discord API');
      }
      
      console.log('Successfully synced Discord message:', response.data);
      return response;
    } catch (error) {
      console.error('Error syncing Discord message:', error);
      
      // Check if this is a response with user-friendly error details
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        
        // If the server sent a user-friendly error message, use it
        if (errorData.userFriendly) {
          const friendlyError = new Error(errorData.details || errorData.error);
          friendlyError.code = errorData.code;
          friendlyError.status = error.response.status;
          friendlyError.userFriendly = true;
          throw friendlyError;
        }
      }
      
      throw error;
    }
  },

  // Get full details of a Discord message including reactions
  getMessageDetails: async (messageId, channelId = null) => {
    try {
      if (!messageId) {
        throw new Error('Message ID is required');
      }

      // Make API call to get message details
      // If channelId is provided, pass it as a param, otherwise the server will use the default briefing channel
      const params = {};
      if (channelId) {
        params.channelId = channelId;
      }
      
      const response = await apiClient.get(`/discord/messages/${messageId}`, { params });
      
      if (!response || !response.data) {
        throw new Error('No data received from Discord API');
      }
      
      // Check if the response contains an error property
      if (response.data.error) {
        // If the server returned an error in the response body, throw it
        const error = new Error(response.data.error);
        error.help = response.data.help;
        error.channelId = response.data.channelId;
        throw error;
      }
      
      // Process the message data to extract emojis
      let extractedEmojis = [];
      
      // Log the raw reactions data to help with debugging
      console.log('Raw reactions data:', response.data.reactions);
      
      // Extract emojis from reactions with improved handling
      if (response.data.reactions && Array.isArray(response.data.reactions)) {
        extractedEmojis = response.data.reactions
          .filter(r => r && (r.emoji || r.name))
          .map(r => {
            // Handle different emoji formats
            if (r.emoji) {
              if (typeof r.emoji === 'object') {
                // New format where emoji is an object
                return r.emoji.name || r.emoji.id || r.name;
              } else {
                // Old format where emoji might be a string
                return r.emoji;
              }
            } else if (r.name) {
              return r.name;
            }
            return null;
          })
          .filter(Boolean);
      }
      
      // Log the extracted emojis
      console.log('Extracted emojis:', extractedEmojis);
      
      // If no emojis found but we know there are reactions, use fallback
      if (!extractedEmojis.length && response.data.reactions && 
          Array.isArray(response.data.reactions) && 
          response.data.reactions.length > 0) {
        // Use reaction count to create placeholder emojis
        extractedEmojis = response.data.reactions.map((_, i) => `reaction_${i+1}`);
        console.log('Using fallback emojis:', extractedEmojis);
      }
      
      // Add the extracted emojis to the response
      response.data.emojiReactions = extractedEmojis;
      
      return response.data;
    } catch (error) {
      console.error('Error fetching message details:', error);
      throw error;
    }
  },

  // Send briefing message
  sendCampaignBriefing: async (campaignId, briefing) => {
    try {
      const response = await apiClient.post(endpoints.discord.campaigns.briefing(campaignId), {
        briefing
      });
      return response.data;
    } catch (error) {
      console.error('Error sending campaign briefing:', error);
      throw error;
    }
  },

  // Get Discord settings
  getSettings: async () => {
    try {
      const response = await apiClient.get(endpoints.discord.settings);
      return response.data;
    } catch (error) {
      console.error('Error getting Discord settings:', error);
      throw error;
    }
  },

  // Save Discord settings
  updateSettings: async (settings) => {
    try {
      const response = await apiClient.post(endpoints.discord.settings, settings);
      return response.data;
    } catch (error) {
      console.error('Error updating Discord settings:', error);
      throw error;
    }
  },

  // Update creator statuses without Discord role assignment
  updateCreatorStatuses: async (campaignId, creatorIds, status, roles) => {
    try {
      console.log(`Updating creator statuses for campaign ${campaignId}`);
      const response = await apiClient.post(`/campaigns/${campaignId}/discord/update-statuses`, {
        creatorIds,
        status,
        roles
      });
      
      if (!response || !response.data) {
        throw new Error('No response received when updating creator statuses');
      }
      
      return response.data;
    } catch (error) {
      console.error('Error updating creator statuses:', error);
      throw error;
    }
  },
};

export default discordService;
